/**
 * Axios instance configured to use XMLHttpRequest with automatic retry
 * This bypasses the rrweb fetch interceptor that corrupts responses
 * Includes: exponential backoff, connection recovery, and request queuing
 */
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL is now imported from centralized config
// In production (newaudia.com), it's hardcoded to https://newaudia.com
// In preview/dev, it uses REACT_APP_BACKEND_URL from .env

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,      // 1 second initial delay
  maxDelay: 10000,      // Max 10 seconds between retries
  retryableErrors: ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET'],
  retryableStatuses: [408, 429, 500, 502, 503, 504, 0] // 0 = network failure
};

// Connection state tracking
let connectionState = {
  isOnline: navigator.onLine,
  lastSuccessfulRequest: Date.now(),
  consecutiveFailures: 0,
  isRecovering: false
};

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('[API] Network back online - will retry pending requests');
    connectionState.isOnline = true;
    connectionState.consecutiveFailures = 0;
  });
  
  window.addEventListener('offline', () => {
    console.log('[API] Network went offline');
    connectionState.isOnline = false;
  });
}

// Calculate delay with exponential backoff and jitter
const getRetryDelay = (attempt) => {
  const exponentialDelay = RETRY_CONFIG.baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 500; // Add 0-500ms jitter
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelay);
};

// Check if error is retryable
const isRetryableError = (error) => {
  // Network errors
  if (RETRY_CONFIG.retryableErrors.includes(error.code)) {
    return true;
  }
  
  // HTTP status codes that warrant retry
  if (error.response && RETRY_CONFIG.retryableStatuses.includes(error.response.status)) {
    return true;
  }
  
  // No response at all (network failure)
  if (!error.response && error.message === 'Network Error') {
    return true;
  }
  
  return false;
};

// Sleep helper
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Custom XHR adapter with retry logic
const xhrAdapterWithRetry = async (config) => {
  let lastError = null;
  const maxRetries = config.noRetry ? 0 : RETRY_CONFIG.maxRetries;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await executeXhrRequest(config);
      
      // Success - reset failure tracking
      connectionState.lastSuccessfulRequest = Date.now();
      connectionState.consecutiveFailures = 0;
      connectionState.isRecovering = false;
      
      return response;
    } catch (error) {
      lastError = error;
      connectionState.consecutiveFailures++;
      
      // Check if we should retry
      if (attempt < maxRetries && isRetryableError(error)) {
        const delay = getRetryDelay(attempt);
        console.warn(`[API] Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(delay)}ms...`, {
          url: config.url,
          error: error.message,
          code: error.code
        });
        
        // Wait before retry
        await sleep(delay);
        
        // If we're offline, wait for online event
        if (!connectionState.isOnline) {
          console.log('[API] Waiting for network to come back online...');
          await waitForOnline(30000); // Wait up to 30 seconds
        }
        
        continue;
      }
      
      // No more retries - throw the error
      throw error;
    }
  }
  
  throw lastError;
};

// Wait for online status
const waitForOnline = (timeout) => {
  return new Promise((resolve) => {
    if (connectionState.isOnline) {
      resolve();
      return;
    }
    
    const timeoutId = setTimeout(() => {
      window.removeEventListener('online', onlineHandler);
      resolve(); // Resolve anyway after timeout
    }, timeout);
    
    const onlineHandler = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('online', onlineHandler);
      resolve();
    };
    
    window.addEventListener('online', onlineHandler);
  });
};

// Core XHR request executor
const executeXhrRequest = (config) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Build URL with params
    let url = config.url;
    if (config.baseURL && !url.startsWith('http')) {
      url = config.baseURL + url;
    }
    
    // Add query params if present
    if (config.params) {
      const params = new URLSearchParams(config.params).toString();
      url += (url.includes('?') ? '&' : '?') + params;
    }
    
    xhr.open(config.method?.toUpperCase() || 'GET', url, true);
    
    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    const headers = config.headers || {};
    Object.keys(headers).forEach(key => {
      if (headers[key] !== undefined && key.toLowerCase() !== 'content-type') {
        xhr.setRequestHeader(key, headers[key]);
      }
    });
    
    // Set timeout (default 30 seconds)
    xhr.timeout = config.timeout || 30000;
    
    xhr.onload = () => {
      let responseData = xhr.responseText;
      try {
        responseData = JSON.parse(xhr.responseText);
      } catch (e) {
        // Keep as text if not JSON
      }
      
      const response = {
        data: responseData,
        status: xhr.status,
        statusText: xhr.statusText,
        headers: {},
        config: config,
        request: xhr
      };
      
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response);
      } else {
        const error = new Error(`Request failed with status ${xhr.status}`);
        error.response = response;
        error.config = config;
        reject(error);
      }
    };
    
    xhr.onerror = (event) => {
      console.error('[API] Network error:', config.url);
      const error = new Error('Network Error');
      error.config = config;
      error.code = 'ERR_NETWORK';
      reject(error);
    };
    
    xhr.ontimeout = () => {
      console.error('[API] Request timeout:', config.url);
      const error = new Error('Timeout');
      error.config = config;
      error.code = 'ECONNABORTED';
      reject(error);
    };
    
    xhr.onabort = () => {
      const error = new Error('Request aborted');
      error.config = config;
      error.code = 'ERR_CANCELED';
      reject(error);
    };
    
    // Send data
    if (config.data) {
      const dataStr = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
      xhr.send(dataStr);
    } else {
      xhr.send();
    }
  });
};

// Create axios instance with custom retry adapter
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  adapter: xhrAdapterWithRetry,
  timeout: 30000
});

// Add request interceptor to attach auth token and active role
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Include X-Active-Role header for multi-role support
    const activeRole = sessionStorage.getItem('activeRole');
    if (activeRole) {
      config.headers['X-Active-Role'] = activeRole;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
// Import global backend status reporter
import { reportGlobalBackendError, reportGlobalBackendSuccess } from '../contexts/BackendStatusContext';

// Backend unavailable status codes that trigger global modal
const BACKEND_UNAVAILABLE_CODES = [500, 502, 503, 504, 520, 521, 522, 523, 524];

// CRITICAL: Only logout on 401/403, NEVER on 5xx errors
// 5xx = backend instability, not auth failure
api.interceptors.response.use(
  (response) => {
    // Report successful response to clear error state
    reportGlobalBackendSuccess();
    return response;
  },
  (error) => {
    const status = error.response?.status || 0;
    const url = error.config?.url || 'unknown';
    
    // Handle 401 unauthorized - token expired or invalid
    // EXCEPT: endpoints that use 401 semantically for credential re-verification
    // (e.g., /kiosk/end-session uses 401 to mean "wrong admin password" — that
    // is a per-request error, not a session expiry). Allow the calling
    // component to handle those locally.
    const PASSWORD_REVERIFY_PATHS = ['/kiosk/end-session'];
    const isPasswordReverify = PASSWORD_REVERIFY_PATHS.some(p => url.includes(p));

    if (status === 401 && !isPasswordReverify) {
      // Don't redirect if already on login page
      if (!window.location.pathname.includes('/login')) {
        console.warn('[API] 401 Unauthorized - clearing token and redirecting to login');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
      }
    }
    
    // Handle 403 forbidden - access revoked
    if (status === 403) {
      console.warn('[API] 403 Forbidden - access denied');
      // Don't auto-logout, let the UI handle it
    }
    
    // 5xx errors: DO NOT logout - backend instability, not auth failure
    // Report to global backend status for modal display
    if (BACKEND_UNAVAILABLE_CODES.includes(status) || status === 0) {
      console.warn(`[API] ${status || 'Network'} Error on ${url} - reporting to BackendStatus`);
      reportGlobalBackendError(status, url, error.message);
    }
    
    return Promise.reject(error);
  }
);

// Export connection state checker
export const getConnectionState = () => ({
  ...connectionState,
  timeSinceLastSuccess: Date.now() - connectionState.lastSuccessfulRequest
});

// Export manual retry trigger for failed requests
export const retryWithBackoff = async (requestFn, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries) {
        const delay = getRetryDelay(i);
        console.log(`[API] Retry ${i + 1}/${maxRetries} in ${Math.round(delay)}ms`);
        await sleep(delay);
      }
    }
  }
  throw lastError;
};

export default api;
export { BACKEND_URL };
